/**
 * Utility functions for parsing date strings in various formats
 */

/**
 * Parse ordinal date strings (1st, 2nd, 3rd, 4th, etc.) to day numbers
 * @param dateString - String like "1st", "5th", "10th", "15th"
 * @returns Day number (1-31) or null if invalid
 */
export function parseOrdinalDate(dateString: string): number | null {
  const trimmed = dateString.trim().toLowerCase();
  
  // Remove common suffixes and extract number
  const match = trimmed.match(/^(\d+)(st|nd|rd|th)?$/);
  
  if (!match) return null;
  
  const day = parseInt(match[1], 10);
  
  // Validate day range
  if (day < 1 || day > 31) return null;
  
  return day;
}

/**
 * Parse various date formats and return day numbers
 * @param dateStrings - Array of date strings
 * @returns Array of valid day numbers
 */
export function parseBulkDates(dateStrings: string[]): number[] {
  const validDays: number[] = [];
  
  for (const dateStr of dateStrings) {
    const day = parseOrdinalDate(dateStr);
    if (day !== null) {
      validDays.push(day);
    }
  }
  
  // Remove duplicates and sort
  return Array.from(new Set(validDays)).sort((a, b) => a - b);
}

/**
 * Convert day numbers to actual dates within a specific month/year
 * @param days - Array of day numbers
 * @param month - Month name (e.g., "January")
 * @param year - Year number
 * @returns Array of formatted date strings (YYYY-MM-DD)
 */
export function daysToDates(days: number[], month: string, year: number): string[] {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const monthIndex = monthNames.indexOf(month);
  if (monthIndex === -1) return [];
  
  const validDates: string[] = [];
  
  for (const day of days) {
    try {
      // Create date in UTC and check if it's valid for the month/year
      const date = new Date(Date.UTC(year, monthIndex, day));
      
      // Check if the date is valid (handles cases like Feb 30th) using UTC methods
      if (date.getUTCFullYear() === year && 
          date.getUTCMonth() === monthIndex && 
          date.getUTCDate() === day) {
        
        // Format as YYYY-MM-DD using original calendar day components
        const formattedDate = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        validDates.push(formattedDate);
      }
    } catch (error) {
      // Skip invalid dates
      console.warn(`Invalid date: ${day}/${monthIndex + 1}/${year}`);
    }
  }
  
  return validDates;
}

/**
 * Parse a date string in various formats
 * @param dateString - Date string in MMDDYYYY, MM/DD/YYYY, MM-DD-YYYY, or ordinal format
 * @param month - Target month name (used for ordinal format)
 * @param year - Target year (used for ordinal format)
 * @returns Formatted date string (YYYY-MM-DD) or null if invalid
 */
function parseDateString(dateString: string, month: string, year: number): string | null {
  const trimmed = dateString.trim();
  
  // Try MMDDYYYY format (8 digits: 01052024)
  const mmddyyyyMatch = trimmed.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (mmddyyyyMatch) {
    const monthNum = parseInt(mmddyyyyMatch[1], 10);
    const day = parseInt(mmddyyyyMatch[2], 10);
    const yearNum = parseInt(mmddyyyyMatch[3], 10);
    
    if (monthNum >= 1 && monthNum <= 12 && day >= 1 && day <= 31) {
      try {
        // Validate date using UTC to avoid timezone issues
        const date = new Date(Date.UTC(yearNum, monthNum - 1, day));
        if (date.getUTCFullYear() === yearNum && date.getUTCMonth() === monthNum - 1 && date.getUTCDate() === day) {
          // Format using original parsed values (calendar day components)
          return `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      } catch (e) {
        return null;
      }
    }
  }
  
  // Try MM/DD/YYYY format (01/05/2024)
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const monthNum = parseInt(slashMatch[1], 10);
    const day = parseInt(slashMatch[2], 10);
    const yearNum = parseInt(slashMatch[3], 10);
    
    if (monthNum >= 1 && monthNum <= 12 && day >= 1 && day <= 31) {
      try {
        // Validate date using UTC to avoid timezone issues
        const date = new Date(Date.UTC(yearNum, monthNum - 1, day));
        if (date.getUTCFullYear() === yearNum && date.getUTCMonth() === monthNum - 1 && date.getUTCDate() === day) {
          // Format using original parsed values (calendar day components)
          return `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      } catch (e) {
        return null;
      }
    }
  }
  
  // Try MM-DD-YYYY format (01-05-2024)
  const dashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashMatch) {
    const monthNum = parseInt(dashMatch[1], 10);
    const day = parseInt(dashMatch[2], 10);
    const yearNum = parseInt(dashMatch[3], 10);
    
    if (monthNum >= 1 && monthNum <= 12 && day >= 1 && day <= 31) {
      try {
        // Validate date using UTC to avoid timezone issues
        const date = new Date(Date.UTC(yearNum, monthNum - 1, day));
        if (date.getUTCFullYear() === yearNum && date.getUTCMonth() === monthNum - 1 && date.getUTCDate() === day) {
          // Format using original parsed values (calendar day components)
          return `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      } catch (e) {
        return null;
      }
    }
  }
  
  // Try ordinal format (5th, 10th) - uses month/year context
  const day = parseOrdinalDate(trimmed);
  if (day !== null) {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthIndex = monthNames.indexOf(month);
    if (monthIndex !== -1) {
      try {
        // Validate date using UTC to avoid timezone issues
        const date = new Date(Date.UTC(year, monthIndex, day));
        if (date.getUTCFullYear() === year && date.getUTCMonth() === monthIndex && date.getUTCDate() === day) {
          // Format using original parsed values (calendar day components)
          return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      } catch (e) {
        return null;
      }
    }
  }
  
  return null;
}

/**
 * Parse bulk date input and return formatted dates
 * @param input - Raw input string from textarea
 * @param month - Target month name (used for ordinal format)
 * @param year - Target year (used for ordinal format)
 * @returns Array of formatted date strings (YYYY-MM-DD)
 */
export function parseBulkDateInput(input: string, month: string, year: number): string[] {
  const lines = input.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  const validDates: string[] = [];
  
  for (const line of lines) {
    const parsedDate = parseDateString(line, month, year);
    if (parsedDate !== null) {
      validDates.push(parsedDate);
    }
  }
  
  // Remove duplicates and sort
  return Array.from(new Set(validDates)).sort();
}
